import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Download,
  FileCheck2,
  FileSpreadsheet,
  History,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useApi } from "../App.jsx";
import {
  CopyableDate,
  DataTable,
  DataTableAction,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTablePrimaryCell,
  DataTableRow,
  DataTableStatus,
  DataTableValue,
  EmptyState,
  FeedbackBanner,
  LoadingBlock,
  PageHeader,
  SectionHeader,
  ConfirmDialog,
  useRouter,
} from "@tribunalea/ui";

const statusLabels = {
  preparing: "Preparando archivos",
  queued: "En cola",
  extracting_text: "Leyendo documentos",
  extracting_fields: "Extrayendo datos",
  awaiting_review: "Para revisar",
  generating_output: "Generando archivo",
  completed: "Completado",
  failed: "Con error",
};

const processingStatuses = new Set([
  "preparing",
  "queued",
  "extracting_text",
  "extracting_fields",
  "generating_output",
]);

const statusTones = {
  awaiting_review: "warning",
  completed: "success",
  failed: "danger",
};

function formatDate(value) {
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function DashboardPage({ access, view = "history" }) {
  const api = useApi();
  const { navigate } = useRouter();
  const [state, setState] = useState({
    loading: true,
    templates: [],
    runs: [],
    error: "",
    notice: "",
    pagination: { page: 1, pages: 1, total: 0 },
  });
  const [filters, setFilters] = useState({
    query: "",
    status: "all",
    archived: false,
    page: 1,
  });
  const [runToDelete, setRunToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exportingId, setExportingId] = useState("");
  const importInputRef = useRef(null);
  const canManageTemplates = !access?.workspace?.institutional
    || access.workspace.role === "org:admin";

  useEffect(() => {
    let active = true;
    setState((current) => ({
      ...current,
      loading: true,
      error: "",
      notice: "",
    }));
    const request = view === "templates"
      ? api.listTemplates()
      : api.listRuns({ ...filters, limit: 10 });
    request
      .then((data) => {
        if (!active) return;
        if (view === "templates") {
          setState((current) => ({
            ...current,
            loading: false,
            templates: data,
            error: "",
          }));
          return;
        }
        const runData = data;
        const runs = Array.isArray(runData) ? runData : runData.items;
        const pagination = Array.isArray(runData)
          ? { page: 1, pages: 1, total: runData.length }
          : runData.pagination;
        setState((current) => ({
          ...current,
          loading: false,
          runs,
          pagination,
          error: "",
        }));
      })
      .catch((error) => {
        if (active) setState((current) => ({
          ...current,
          loading: false,
          error: error.message,
        }));
      });
    return () => {
      active = false;
    };
  }, [api, filters, view]);

  const deleteRun = async () => {
    if (!runToDelete || deleting) return;
    setDeleting(true);
    try {
      await api.deleteRun(runToDelete.id);
      setState((current) => ({
        ...current,
        runs: current.runs.filter((run) => run.id !== runToDelete.id),
        error: "",
      }));
      setRunToDelete(null);
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
      setRunToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const importTemplate = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file || importing) return;
    setImporting(true);
    setState((current) => ({ ...current, error: "", notice: "" }));
    try {
      const imported = await api.importTemplate(file);
      setState((current) => ({
        ...current,
        templates: [
          imported,
          ...current.templates.filter(({ id }) => id !== imported.id),
        ],
        error: "",
        notice: `Importamos “${imported.name}” correctamente.`,
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error.message,
        notice: "",
      }));
    } finally {
      input.value = "";
      setImporting(false);
    }
  };

  const exportTemplate = async (template) => {
    if (exportingId) return;
    setExportingId(template.id);
    setState((current) => ({ ...current, error: "", notice: "" }));
    try {
      await api.exportTemplate(template.id);
      setState((current) => ({
        ...current,
        notice: `Exportamos “${template.name}” como JSON.`,
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error.message,
        notice: "",
      }));
    } finally {
      setExportingId("");
    }
  };

  return (
    <div className="page processor-dashboard-page">
      <PageHeader
        hero
        eyebrow="Instrumenta · Procesador documental"
        title="Convertí documentos en datos verificables."
        description={view === "history"
          ? "Retomá procesamientos anteriores o empezá uno nuevo reutilizando tus plantillas."
          : "Configurá los formatos reutilizables que usás para extraer y ordenar información."}
        actions={(
          <button
            data-justiniano-target="processor-start"
            className="primary-button"
            onClick={() => navigate("/procesador-documental/procesar")}
          >
            <Sparkles size={18} /> Procesar documentos
          </button>
        )}
      />

      {state.error && <FeedbackBanner>{state.error}</FeedbackBanner>}
      {state.notice && (
        <FeedbackBanner tone="success">{state.notice}</FeedbackBanner>
      )}

      <nav className="processor-dashboard-tabs" aria-label="Secciones del procesador documental">
        <button
          type="button"
          className={view === "history" ? "is-active" : ""}
          aria-current={view === "history" ? "page" : undefined}
          onClick={() => navigate("/procesador-documental")}
        >
          <History size={17} /> Actividad
        </button>
        <button
          type="button"
          className={view === "templates" ? "is-active" : ""}
          aria-current={view === "templates" ? "page" : undefined}
          onClick={() => navigate("/procesador-documental/plantillas")}
        >
          <FileSpreadsheet size={17} /> Plantillas
        </button>
      </nav>

      {view === "templates" ? (
        <section className="section-block processor-dashboard-panel is-templates">
          <SectionHeader
            eyebrow="Plantillas"
            title="Formatos reutilizables"
            actions={canManageTemplates ? (
              <div className="processor-template-actions">
                <input
                  ref={importInputRef}
                  hidden
                  type="file"
                  accept=".json,application/json"
                  onChange={importTemplate}
                />
                <button
                  type="button"
                  className="secondary-button"
                  disabled={importing}
                  onClick={() => importInputRef.current?.click()}
                >
                  <Upload size={17} />
                  {importing ? "Importando…" : "Importar JSON"}
                </button>
                <button
                  data-justiniano-target="processor-new-template"
                  className="secondary-button"
                  onClick={() => navigate("/procesador-documental/plantillas/nueva")}
                >
                  <Plus size={17} /> Nueva plantilla
                </button>
              </div>
            ) : null}
          />
          {state.loading ? (
            <LoadingBlock>Cargando plantillas…</LoadingBlock>
          ) : state.templates.length ? (
            <div className="template-grid processor-template-grid">
              {state.templates.map((template) => (
                <article
                  className="template-card processor-template-card"
                  key={template.id}
                >
                  <button
                    type="button"
                    className="processor-template-card-main"
                    disabled={!canManageTemplates}
                    title={canManageTemplates
                      ? "Configurar plantilla"
                      : "Solo los administradores pueden configurar plantillas"}
                    onClick={() => navigate(`/procesador-documental/plantillas/${template.id}`)}
                  >
                    <span className="card-icon"><FileSpreadsheet size={22} /></span>
                    <span className="template-card-copy">
                      <strong>{template.name}</strong>
                      <small>
                        {template.version?.outputKind === "display"
                          ? "Resultado en pantalla"
                          : "Completa Excel"}{" "}
                        · Versión {template.currentVersion} ·{" "}
                        {template.version?.fields?.length || 0}{" "}
                        {(template.version?.fields?.length || 0) === 1
                          ? "campo"
                          : "campos"}
                      </small>
                    </span>
                    {canManageTemplates && <ArrowRight size={18} />}
                  </button>
                  <button
                    type="button"
                    className="icon-button processor-template-export"
                    aria-label={`Exportar ${template.name} como JSON`}
                    title="Exportar como JSON"
                    disabled={Boolean(exportingId)}
                    onClick={() => exportTemplate(template)}
                  >
                    <Download size={17} />
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={FileSpreadsheet}
              title="Creá tu primera plantilla"
              description="Definí qué información querés extraer y elegí si se muestra en pantalla o completa una planilla."
              action={canManageTemplates ? (
                <button
                  className="primary-button"
                  onClick={() => navigate("/procesador-documental/plantillas/nueva")}
                >
                  Crear plantilla
                </button>
              ) : null}
            />
          )}
        </section>
      ) : (
        <section className="section-block processor-dashboard-panel is-activity">
          <SectionHeader eyebrow="Actividad" title="Historial de procesamientos" />
          <div className="processor-history-filters">
            <label>
              <Search size={16} />
              <input
                type="search"
                value={filters.query}
                placeholder="Buscar plantilla, documento u operador"
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  query: event.target.value,
                  page: 1,
                }))}
              />
            </label>
            <select
              value={filters.status}
              onChange={(event) => setFilters((current) => ({
                ...current,
                status: event.target.value,
                page: 1,
              }))}
            >
              <option value="all">Todos los estados</option>
              <option value="awaiting_review">Para revisar</option>
              <option value="completed">Completados</option>
              <option value="failed">Con error</option>
            </select>
            <select
              value={String(filters.archived)}
              onChange={(event) => setFilters((current) => ({
                ...current,
                archived: event.target.value === "true",
                page: 1,
              }))}
            >
              <option value="false">Historial activo</option>
              <option value="true">Archivados</option>
            </select>
          </div>
          {state.loading ? (
            <LoadingBlock>Cargando procesamientos…</LoadingBlock>
          ) : state.runs.length ? (
            <DataTable
              ariaLabel="Historial de procesamientos documentales"
              className="processor-history-table"
              density="compact"
              minWidth="820px"
            >
              <DataTableHead>
                <DataTableRow>
                  <DataTableHeaderCell>Procesamiento</DataTableHeaderCell>
                  <DataTableHeaderCell>Estado</DataTableHeaderCell>
                  <DataTableHeaderCell priority="secondary">Última modificación</DataTableHeaderCell>
                  <DataTableHeaderCell align="end">Acciones</DataTableHeaderCell>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {state.runs.map((run) => {
                  const title = run.template?.name || "Plantilla";
                  const version = run.version > 0
                    ? `Versión ${run.version}`
                    : "Borrador";
                  return (
                    <DataTableRow
                      key={run.id}
                      label={`Abrir ${title}`}
                      onActivate={() => navigate(`/procesador-documental/procesamientos/${run.id}`)}
                    >
                      <DataTableCell>
                        <DataTablePrimaryCell
                          icon={<FileCheck2 size={17} />}
                          eyebrow={`${run.inputCount} PDF${run.inputCount === 1 ? "" : "s"} · ${version}`}
                          title={title}
                          meta={`Operador ${run.createdByName || "Usuario"}`}
                        />
                      </DataTableCell>
                      <DataTableCell>
                        <DataTableStatus
                          label={statusLabels[run.status] || run.status}
                          tone={statusTones[run.status] || "neutral"}
                        />
                      </DataTableCell>
                      <DataTableCell priority="secondary">
                        <DataTableValue
                          primary={(
                            <CopyableDate as="span" value={run.updatedAt || run.createdAt}>
                              {formatDate(run.updatedAt || run.createdAt)}
                            </CopyableDate>
                          )}
                        />
                      </DataTableCell>
                      <DataTableCell align="end">
                        {!filters.archived && run.permissions?.canArchive !== false && (
                          <DataTableAction
                            label={`${run.workspaceType === "organization" ? "Archivar" : "Eliminar"} ${title}`}
                            icon={<Trash2 size={14} />}
                            disabled={processingStatuses.has(run.status)}
                            onClick={() => setRunToDelete(run)}
                          >
                            {run.workspaceType === "organization" ? "Archivar" : "Eliminar"}
                          </DataTableAction>
                        )}
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </DataTableBody>
            </DataTable>
          ) : (
            <EmptyState
              icon={History}
              title="Todavía no hay procesamientos"
              description="Cuando proceses documentos, vas a poder retomarlos desde este historial."
            />
          )}
          {state.pagination.pages > 1 && (
            <div className="processor-history-pagination">
              <button
                type="button"
                disabled={filters.page <= 1}
                onClick={() => setFilters((current) => ({
                  ...current,
                  page: current.page - 1,
                }))}
              >
                <ChevronLeft size={16} /> Anterior
              </button>
              <span>Página {state.pagination.page} de {state.pagination.pages}</span>
              <button
                type="button"
                disabled={filters.page >= state.pagination.pages}
                onClick={() => setFilters((current) => ({
                  ...current,
                  page: current.page + 1,
                }))}
              >
                Siguiente <ChevronRight size={16} />
              </button>
            </div>
          )}
        </section>
      )}
      {runToDelete && (
        <ConfirmDialog
          title={runToDelete.workspaceType === "organization"
            ? "Archivar procesamiento"
            : "Eliminar procesamiento"}
          description={runToDelete.workspaceType === "organization"
            ? "El procesamiento dejará de aparecer en el historial activo y conservará su trazabilidad."
            : "Se eliminarán este registro, sus PDFs y el resultado generado. Esta acción no se puede deshacer."}
          confirmLabel={deleting
            ? "Guardando…"
            : runToDelete.workspaceType === "organization"
              ? "Archivar procesamiento"
              : "Eliminar procesamiento"}
          onConfirm={deleteRun}
          onClose={() => {
            if (!deleting) setRunToDelete(null);
          }}
          busy={deleting}
        />
      )}
    </div>
  );
}
